/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Input as AntInput, InputRef } from 'antd'
import React, { useState, forwardRef } from 'react'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'

/**
 * TextInput component for form fields with various customization options
 * @component
 */
type TextInputProps = {
  /** Field name for error handling and identification */
  name: string
  /** Current input value */
  value: string
  /** Function to update the input value */
  setValue: (value: string) => void
  /** Object containing field validation errors */
  fieldErrors: any
  /** Custom error message */
  customError: any
  /** Label text displayed above the input */
  label: string
  /** Optional helper text rendered under the label */
  subtitle?: string
  /** Input type (text, password, number, etc.) */
  type?: string
  /** Maximum character length */
  maxLength?: number
  /** Whether the input is disabled */
  disabled?: boolean
  /** A control on the input's row, after it (e.g. an Edit button) */
  action?: React.ReactNode
  /** HTML autocomplete attribute */
  autoComplete?:
    | 'on'
    | 'off'
    | 'name'
    | 'honorific-prefix'
    | 'given-name'
    | 'additional-name'
    | 'family-name'
    | 'honorific-suffix'
    | 'nickname'
    | 'username'
    | 'new-password'
    | 'current-password'
    | 'one-time-code'
    | 'organization-title'
    | 'organization'
    | 'street-address'
    | 'address-line1'
    | 'address-line2'
    | 'address-line3'
    | 'address-level4'
    | 'address-level3'
    | 'address-level2'
    | 'address-level1'
    | 'country'
    | 'country-name'
    | 'postal-code'
    | 'cc-name'
    | 'cc-given-name'
    | 'cc-additional-name'
    | 'cc-family-name'
    | 'cc-number'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-csc'
    | 'cc-type'
    | 'transaction-currency'
    | 'transaction-amount'
    | 'language'
    | 'bday'
    | 'bday-day'
    | 'bday-month'
    | 'bday-year'
    | 'sex'
    | 'url'
    | 'photo'
  /** Function called when input loses focus */
  onBlur?: () => void
  /** Minimum value for number inputs */
  min?: number
  /** Maximum value for number inputs */
  max?: number
  /** Placeholder text */
  placeholder?: string
  /** Content to display after the input */
  addonAfter?: string
  /** Custom style for the input */
  /** Custom CSS styles using emotion css */
  style?: SerializedStyles
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Success state for showing green border and check icon */
  success?: boolean
  /** Error state for showing red border and close icon */
  error?: boolean
  /** Function called when a key is pressed */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /** Autofocus the input on mount */
  autoFocus?: boolean
  /** Whether the field is required (shows asterisk after label) */
  required?: boolean
  /**
   * Keep the label for assistive tech only (it stays the input's aria-label). For
   * repeated rows whose columns are named once, above them.
   */
  hideLabel?: boolean
  /**
   * `large` puts the label and the value at body size (16px), for a dialog whose
   * copy is at body size. Default is the house 14px. `compact` is a 32px field
   * for a grid row, where a 40px one would make the row taller than its peers.
   */
  size?: 'default' | 'large' | 'compact'
}

export const TextInput = forwardRef<InputRef, TextInputProps>(
  (
    {
      name,
      value,
      setValue,
      fieldErrors,
      customError,
      label,
      subtitle,
      type = 'text',
      maxLength = 255,
      disabled = false,
      action,
      autoComplete = 'off',
      onBlur,
      min,
      max,
      placeholder,
      addonAfter,
      style,
      tabIndex,
      success = false,
      error = false,
      onKeyDown,
      autoFocus,
      required = false,
      hideLabel = false,
      size = 'default',
    },
    ref
  ) => {
    // State to toggle password visibility
    const [passwordVisible, setPasswordVisible] = useState(false)

    // Determine the actual input type based on password visibility
    const inputType = type === 'password' && passwordVisible ? 'text' : type

    // Password visibility toggle icon
    const passwordIcon =
      type === 'password' ? (
        <span
          onClick={() => setPasswordVisible(!passwordVisible)}
          css={css`
            cursor: pointer;
            color: ${colors.WHITE};
          `}
        >
          {passwordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </span>
      ) : null

    // Success icon
    const successIcon = success ? (
      <span
        css={css`
          color: ${colors.SUCCESS_300};
        `}
      >
        <CheckCircleOutlined />
      </span>
    ) : null

    // Error icon
    const errorIcon = error ? (
      <span
        css={css`
          color: ${colors.ERROR_300};
        `}
      >
        <CloseCircleOutlined />
      </span>
    ) : null

    // Determine suffix icon - error takes precedence over success, success over password
    const suffixIcon = errorIcon || successIcon || passwordIcon

    const input = (
      <AntInput
        ref={ref}
        aria-label={label}
        addonAfter={addonAfter}
        placeholder={placeholder}
        min={min}
        max={max}
        autoFocus={autoFocus}
        status={
          fieldErrors[name] ||
          (Array.isArray(fieldErrors) && fieldErrors?.includes(name))
            ? 'error'
            : ''
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
        type={inputType}
        maxLength={maxLength}
        disabled={disabled}
        autoComplete={autoComplete}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        suffix={suffixIcon}
        tabIndex={tabIndex}
        css={css`
          font-size: ${size === 'large' ? 16 : 14}px !important;
          height: ${size === 'compact' ? 32 : 40}px !important;
          border-radius: ${size === 'compact' ? 6 : 8}px;
          border: 1px solid
            ${error
              ? colors.ERROR_300
              : success
              ? colors.SUCCESS_300
              : colors.SECONDARY_100};
          background: ${colors.SECONDARY_200};
          & input::placeholder {
            text-transform: capitalize;
          }
          ${success &&
          !error &&
          css`
            border: 1px solid ${colors.SUCCESS_300} !important;
            box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2) !important;
          `}
          ${error &&
          css`
            border: 1px solid ${colors.ERROR_300} !important;
            box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
          `}
            ${disabled &&
          css`
            color: ${colors.NEUTRAL_100};
          `}
            ${!disabled &&
          css`
            color: ${colors.WHITE};
          `}
            ${style}
        `}
      />
    )

    return (
      <>
        {hideLabel ? null : (
          <Text
            variant={size === 'large' ? 'paragraphMedium' : 'paragraphSmall'}
            weight='regular'
            color={colors.WHITE}
            style={`
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-word;
          opacity: ${disabled ? 0.45 : 1};
        `}
          >
            {label}
            {required && (
              <span
                css={css`
                  margin-left: 4px;
                `}
              >
                *
              </span>
            )}
          </Text>
        )}
        {subtitle && !hideLabel ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {subtitle}
          </Text>
        ) : null}
        {/* Wrapped only with an action, so every other caller's markup is unchanged */}
        {action ? (
          <div css={actionRowStyle}>
            {input}
            {action}
          </div>
        ) : (
          input
        )}
        <ErrorLabel
          fieldErrors={fieldErrors}
          customError={customError}
          name={name}
        />
      </>
    )
  }
)

const actionRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`
